import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Custom element that manages the finishes block:
 * - Opens dialog when a thumbnail is clicked
 * - Populates dialog with product details
 * - Allows switching products from the popup slider list
 * - Handles scroll arrow navigation on finishes-scroller
 */
class FinishesBlock extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // --- Scroller arrows ---
    const prevBtn = /** @type {HTMLElement | null} */ (event.target.closest('[data-scroller-prev]'));
    if (prevBtn) {
      const track = this.#getTrackFor(prevBtn);
      track?.scrollBy({ left: -(track.clientWidth * 0.8), behavior: 'smooth' });
      return;
    }

    const nextBtn = /** @type {HTMLElement | null} */ (event.target.closest('[data-scroller-next]'));
    if (nextBtn) {
      const track = this.#getTrackFor(nextBtn);
      track?.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }

    // --- Thumbnail click ---
    const thumbnail = /** @type {HTMLElement | null} */ (event.target.closest('[data-finish-product]'));
    if (!thumbnail) return;

    const isInsideDialog = !!thumbnail.closest('dialog');
    this.#populateDialog(thumbnail);

    if (!isInsideDialog) {
      const dialog = this.querySelector('dialog-component');
      dialog?.showDialog();
    }
  };

  /**
   * Gets the scroller track closest to the clicked arrow.
   * @param {HTMLElement} arrow
   * @returns {HTMLElement | null}
   */
  #getTrackFor(arrow) {
    return /** @type {HTMLElement | null} */ (
      arrow.closest('.finishes-scroller')?.querySelector('[data-scroller-track]') ?? null
    );
  }

  /** @param {HTMLElement} button */
  #populateDialog(button) {
    const dialog = this.querySelector('dialog-component');
    if (!dialog) return;

    const {
      finishTitle,
      finishImage,
      finishDescription,
      finishVariantId,
      finishProductId,
      finishProductUrl,
    } = button.dataset;

    // Update image
    const imageEl = dialog.querySelector('.popup-link__image');
    if (imageEl) {
      imageEl.innerHTML = finishImage
        ? `<img src="${finishImage}" alt="${finishTitle || ''}">`
        : '';
    }

    // Update title
    const titleEl = dialog.querySelector('.popup-link__title');
    if (titleEl) titleEl.textContent = finishTitle || '';

    // Update description
    const descEl = dialog.querySelector('.popup-link__description');
    if (descEl) descEl.textContent = finishDescription || '';

    // Update product-form-component
    const formComponent = dialog.querySelector('product-form-component');
    if (formComponent) {
      if (finishProductId) formComponent.dataset.productId = finishProductId;
      if (finishProductUrl) formComponent.dataset.productUrl = finishProductUrl;
    }

    // Update hidden variant input
    const variantInput = dialog.querySelector('input[name="id"]');
    if (variantInput && finishVariantId) {
      variantInput.value = finishVariantId;
    }

    // Update active state across all thumbnail lists in the dialog
    dialog.querySelectorAll('[data-finish-product]').forEach((btn) => {
      btn.classList.toggle(
        'finish-thumbnail--active',
        btn.dataset.finishProductId === finishProductId
      );
    });
  }
}

if (!customElements.get('finishes-block')) {
  customElements.define('finishes-block', FinishesBlock);
}